import React, { useState, useEffect } from 'react';
import { capaAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  Filter,
  Edit,
  Calendar as CalendarIcon,
  Loader2,
} from 'lucide-react';
import { format, isPast, parseISO } from 'date-fns';

const CAPAPage = () => {
  const [capas, setCAPAs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCAPA, setSelectedCAPA] = useState(null);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    owner: '',
    due_date: new Date(),
    status: '',
    closure_evidence: '',
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const getFilterValue = (value) => value === 'all' ? '' : value;

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;

      const [capasRes, usersRes] = await Promise.all([
        capaAPI.getAll(params),
        usersAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setCAPAs(capasRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (capa) => {
    setSelectedCAPA(capa);
    setEditForm({
      owner: capa.owner,
      due_date: new Date(capa.due_date),
      status: capa.status,
      closure_evidence: capa.closure_evidence || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateCAPA = async () => {
    if (!selectedCAPA) return;

    setSaving(true);
    try {
      await capaAPI.update(selectedCAPA.id, {
        finding_id: selectedCAPA.finding_id,
        owner: editForm.owner,
        due_date: format(editForm.due_date, 'yyyy-MM-dd'),
        status: editForm.status,
        closure_evidence: editForm.closure_evidence,
      });
      toast.success('CAPA updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update CAPA');
    } finally {
      setSaving(false);
    }
  };

  const getOwnerName = (ownerId) => {
    const user = users.find((u) => u.id === ownerId);
    return user?.name || ownerId;
  };

  const getStatusBadge = (status) => {
    const styles = {
      Open: 'bg-blue-100 text-blue-800 border-blue-200',
      'In Progress': 'bg-amber-100 text-amber-800 border-amber-200',
      Closed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    };
    return <Badge className={`${styles[status]} border`}>{status}</Badge>;
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Closed') return false;
    return isPast(parseISO(dueDate));
  };

  const openCount = capas.filter((c) => c.status === 'Open').length;
  const inProgressCount = capas.filter((c) => c.status === 'In Progress').length;
  const closedCount = capas.filter((c) => c.status === 'Closed').length;
  const overdueCount = capas.filter((c) => isOverdue(c.due_date, c.status)).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="capa-page">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Open</p>
              <p className="metric-value text-blue-500">{openCount}</p>
            </div>
            <Clock size={24} className="text-blue-500" />
          </div>
        </Card>
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">In Progress</p>
              <p className="metric-value text-amber-500">{inProgressCount}</p>
            </div>
            <Clock size={24} className="text-amber-500" />
          </div>
        </Card>
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Closed</p>
              <p className="metric-value text-emerald-500">{closedCount}</p>
            </div>
            <CheckCircle2 size={24} className="text-emerald-500" />
          </div>
        </Card>
        <Card className="metric-card border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Overdue</p>
              <p className="metric-value text-red-500">{overdueCount}</p>
            </div>
            <AlertCircle size={24} className="text-red-500" />
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={() => setStatusFilter('all')}
            >
              <Filter size={16} className="mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* CAPA Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Corrective Action Tracking
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#4A5690] mx-auto" />
            </div>
          ) : capas.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CheckCircle2 size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No CAPA records found</p>
              <p className="text-sm">Create CAPA from findings to track corrective actions</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Finding</th>
                    <th>Owner</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Closure Evidence</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {capas.map((capa) => {
                    const overdue = isOverdue(capa.due_date, capa.status);
                    return (
                      <tr key={capa.id} className={overdue ? 'bg-red-50' : ''}>
                        <td>
                          <div className="max-w-[300px]">
                            <p className="text-sm text-slate-900 truncate">
                              {capa.finding_observation || 'N/A'}
                            </p>
                          </div>
                        </td>
                        <td>{getOwnerName(capa.owner)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className={overdue ? 'text-red-600 font-medium' : ''}>
                              {format(parseISO(capa.due_date), 'MMM d, yyyy')}
                            </span>
                            {overdue && (
                              <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">
                                Overdue
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>{getStatusBadge(capa.status)}</td>
                        <td>
                          <span className="text-sm text-slate-600 truncate max-w-[150px] block">
                            {capa.closure_evidence || '-'}
                          </span>
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClick(capa)}
                            data-testid={`edit-capa-${capa.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Update CAPA</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Owner</Label>
              <Select
                value={editForm.owner}
                onValueChange={(v) => setEditForm({ ...editForm, owner: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(editForm.due_date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editForm.due_date}
                    onSelect={(date) => date && setEditForm({ ...editForm, due_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Closure Evidence</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={editForm.closure_evidence}
                onChange={(e) => setEditForm({ ...editForm, closure_evidence: e.target.value })}
                placeholder="Describe evidence of closure..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleUpdateCAPA}
                className="flex-1 bg-[#4A5690] hover:bg-[#3D477A]"
                disabled={saving}
                data-testid="update-capa-btn"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CAPAPage;
